import { DemoIdUtils, fakeApi } from '@frontend/demo-interceptors';
import { Options } from '@frontend/fetch';
import { LimitedSchemaMutationOptions, useMutation } from '@frontend/react-query-helpers';
import { SchemaCallIntelV2Service, SchemaTaskService } from '../service';
import {
  CallEditableFields,
  CreateSuggestionIO,
  DeleteTaskIO,
  EditAIOutputIO,
  ExtendedUpdateTaskRequest,
  GetCallMetadataIO,
  GetServiceQualityStatsIO,
  SubmitFeedbackIO,
  TransformEditableFieldType,
  UpdateTaskIO,
} from '../types';
import { transformEditableFields } from '../utils/call-intel-utils';
import { mutationKey } from './keys';

export const useSubmitFeedback = <E = unknown, T = SubmitFeedbackIO['output']>(args?: {
  options?: LimitedSchemaMutationOptions<SubmitFeedbackIO, E, T>;
  httpOptions?: Options;
}) => {
  return useMutation({
    mutationKey: mutationKey.submitFeedback,
    mutationFn: (req) => SchemaCallIntelV2Service.SubmitFeedback(req, args?.httpOptions),
    ...args?.options,
  });
};

export const useUpdateTask = <E = unknown, T = UpdateTaskIO['output']>(
  options?: LimitedSchemaMutationOptions<UpdateTaskIO, E, T, never, ExtendedUpdateTaskRequest> & {
    isDemoAccount?: boolean;
  }
) => {
  return useMutation({
    mutationFn: (req) => {
      const { callId, oldStatus, taskType, id, ...serverRequest } = req;

      return SchemaTaskService.UpdateTask({
        ...serverRequest,
        id: options?.isDemoAccount ? DemoIdUtils.addDemoPrefix(id) : id,
      });
    },
    ...options,
  });
};

export const useDeleteTask = <E = unknown, T = DeleteTaskIO['output']>(
  options?: LimitedSchemaMutationOptions<DeleteTaskIO, E, T> & { isDemoAccount?: boolean }
) => {
  return useMutation({
    mutationFn: (req) =>
      SchemaTaskService.DeleteTask({
        ...req,
        id: options?.isDemoAccount ? DemoIdUtils.addDemoPrefix(req.id) : req.id,
      }),
    ...options,
  });
};

export const useEditAiOutput = <E = unknown, T = EditAIOutputIO['output']>(
  options?: LimitedSchemaMutationOptions<EditAIOutputIO, E, T, { type: TransformEditableFieldType }> & {
    isDemoAccount?: boolean;
  }
) => {
  return useMutation({
    mutationFn: (req) => {
      const { _otherOptions, ...rest } = req;
      const type: TransformEditableFieldType = _otherOptions?.type ?? 'edit';

      // type needs to be cast till we fix internal types
      const transformedPayload = {
        ...rest,
        isDemo: options?.isDemoAccount ?? rest.isDemo,
        currentFields: transformEditableFields(rest.currentFields as Partial<CallEditableFields>, type),
        editedFields: transformEditableFields(rest.editedFields as Partial<CallEditableFields>, type),
      };

      return SchemaCallIntelV2Service.EditAIOutput(transformedPayload);
    },
    ...options,
  });
};

export const useCreateSuggestion = <E = unknown, T = CreateSuggestionIO['output']>(
  options?: LimitedSchemaMutationOptions<CreateSuggestionIO, E, T> & { isDemoAccount?: boolean }
) => {
  return useMutation({
    mutationFn: (req) =>
      options?.isDemoAccount
        ? fakeApi<CreateSuggestionIO['output']>({})
        : SchemaCallIntelV2Service.CreateSuggestion(req),
    ...options,
  });
};

export const useDemoCallMetadata = <E = unknown, T = GetCallMetadataIO['output']>(
  options?: LimitedSchemaMutationOptions<GetCallMetadataIO, E, T>
) => {
  return useMutation({
    mutationFn: (req) => SchemaCallIntelV2Service.GetCallMetadataNew(req),
    ...options,
  });
};

export const useGetServiceQualityStats = <E = unknown, T = GetServiceQualityStatsIO['output']>(
  options?: LimitedSchemaMutationOptions<GetServiceQualityStatsIO, E, T>
) => {
  return useMutation({
    mutationFn: (req) => SchemaCallIntelV2Service.GetServiceQualityStats(req),
    ...options,
  });
};
